import type { ComponentProps } from 'react';
import { Button, Dialog, Icon, Sheet, useIsMobile } from '@forecastify/ui';
import { useT } from '../../i18n';
import { usePersonName } from '../person-name';
import { PersonIdentity, PersonScreen } from './person-screen';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ C's ITEM 46 · one person in three frames. `task-surface.tsx`'s shape.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Person gains modal and drawer — fullscreen is the only view today."* A person was reachable
 * only as a route, so every look at one cost the list: its page, its filters and its scroll
 * position, all re-fetched on the way back. Three frames, one body, exactly as the task, the
 * contract and the line item already have:
 *
 *  * **drawer** — a `Sheet` at `min(560px, 96vw)`, the narrow single column;
 *  * **modal** — a bare `Dialog` at `min(1320px, 94vw)` with its own 48px header, the wide grid;
 *  * **full page** — the route, which claims the shell's header and the whole width.
 *
 * ⚠️⚠️ **THE FULL PAGE IS NOT WRAPPED AT ALL HERE**, and that is the difference from its three
 * siblings. `PersonScreen` already draws the page — it claims the chrome through `usePageHeader`
 * and carries `data-screen` — so a `FullPage` frame around it would be a second container adding
 * padding to a screen that already has its own.
 *
 * ⚠️ **`mode` lives with the CALLER**, so `/people/:id` opens straight into `full` and a row click
 * on the list opens whatever item 34's ladder says — which from a list is a modal.
 */
export type PersonMode = 'drawer' | 'modal' | 'full';

export function PersonSurface({
  mode,
  open,
  onModeChange,
  onClose,
  ...person
}: {
  readonly mode: PersonMode;
  readonly open: boolean;
  readonly onModeChange: (mode: PersonMode) => void;
  readonly onClose: () => void;
} & Omit<ComponentProps<typeof PersonScreen>, 'mode'>) {
  const t = useT();
  /* ⚠️ Item 6 · both frames title themselves with the name in the reader's order; the full page
     draws its own header, which composes the same name for itself. */
  const name = usePersonName()(person.person);
  const mobile = useIsMobile();

  if (mode === 'full') return <PersonScreen {...person} mode="full" />;

  /* ⚠️ 17 Oct 2026 · on a phone a modal is the full-screen drawer — the product owner. */
  if (mode === 'modal' && !mobile) {
    return (
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        bare
        /* ⚠️ Item 36's width — the same as every other detail modal. A surface that is narrower
           than its siblings is one somebody has to re-learn. */
        width="min(1320px, 94vw)"
        closeLabel={t('common.close')}
      >
        {/* ⚠️ **`bare`, with a header this file draws** — a `Dialog`'s own header gives a title and
            a close button and nowhere to put minimise and maximise. */}
        <div className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
          {/* ⚠️ Item 48 · the SAME row the full page's header carries. This drew a bare name, so
              one person looked like two different records depending on how you opened them. */}
          <PersonIdentity t={t} person={person.person} name={name} />
          <div className="min-w-0 flex-1" />

          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('task.mode.drawer')}
            title={t('task.mode.drawer')}
            onClick={() => {
              onModeChange('drawer');
            }}
          >
            <Icon name="minimize" size={15} />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('task.mode.full')}
            title={t('task.mode.full')}
            onClick={() => {
              onModeChange('full');
            }}
          >
            <Icon name="maximize" size={15} />
          </Button>
          <Button variant="ghost" size="icon-sm" aria-label={t('common.close')} onClick={onClose}>
            <Icon name="x" size={15} />
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <PersonScreen {...person} mode="modal" />
        </div>
      </Dialog>
    );
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      /* ⚠️ Item 48 · a node, not a string — `Sheet`'s title takes one, so the drawer gets the
         same identity row as the modal and the page rather than a name on its own. */
      title={
        <span className="flex min-w-0 items-center gap-2">
          <PersonIdentity t={t} person={person.person} name={name} />
        </span>
      }
      width="min(560px, 96vw)"
      closeLabel={t('common.close')}
      /* 17 Oct 2026 · no mode buttons on a phone: the drawer is the only frame there. */
      actions={
        mobile
          ? []
          : [
              {
                label: t('task.mode.modal'),
                icon: 'maximize',
                onSelect: () => {
                  onModeChange('modal');
                },
              },
            ]
      }
    >
      <PersonScreen {...person} mode="drawer" />
    </Sheet>
  );
}
